import { useQuery, useMutation, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { folderKeys, touchpointKeys, type BulkMutationMeta } from "./query-keys";
import {
  CampaignFolderDTO,
  CampaignFolderCreateRequest,
  CampaignFolderUpdateRequest,
  GetFoldersParams,
  AddToFolderRequest,
  PagedModelCampaignFolderDTO,
} from "generated/schemas";
import { getCampaignFolder } from "generated/api/campaign-folder";
import { useApiContext, useRequiredApiContext } from "./helpers/api-context";

const folderApi = getCampaignFolder();

const DEFAULT_FOLDERS_SORT: GetFoldersParams["sort"] = ["name,asc"];

/**
 * Hook for fetching a list of campaign folders.
 * Folders are sorted by name ascending by default so dropdowns show them in alphabetical order.
 */
export const useFolders = <TData = PagedModelCampaignFolderDTO>(
  params?: GetFoldersParams & { clientId?: string; campaignId?: string },
  options?: Omit<
    UseQueryOptions<PagedModelCampaignFolderDTO, Error, TData>,
    "queryKey" | "queryFn"
  >,
) => {
  const apiContext = useApiContext();
  // IMPORTANT: params extends API query params with clientId/campaignId overrides.
  // We must not send those ids as query string params.
  const {
    clientId: clientIdOverride,
    campaignId: campaignIdOverride,
    ...restParams
  } = params ?? {};
  const clientId = clientIdOverride ?? apiContext.clientId;
  const campaignId = campaignIdOverride ?? apiContext.campaignId;

  const { page, size, ...unpagedRest } = restParams;
  const queryParams: GetFoldersParams = restParams.unpaged
    ? { ...unpagedRest, sort: restParams.sort ?? DEFAULT_FOLDERS_SORT }
    : { ...restParams, sort: restParams.sort ?? DEFAULT_FOLDERS_SORT };

  return useQuery({
    queryKey: folderKeys.list(clientId ?? "", campaignId ?? "", queryParams),
    queryFn: () => {
      if (!clientId || !campaignId) {
        throw new Error("Context required for useFolders queryFn");
      }
      return folderApi.getFolders({ clientId, campaignId }, queryParams);
    },
    ...options,
    enabled: Boolean(clientId) && Boolean(campaignId) && (options?.enabled ?? true),
  });
};

/**
 * Hook for fetching a single folder
 */
export const useFolder = <TData = CampaignFolderDTO>(
  id: string,
  options?: Omit<UseQueryOptions<CampaignFolderDTO, Error, TData>, "queryKey" | "queryFn">,
) => {
  const { clientId, campaignId } = useRequiredApiContext();

  return useQuery({
    queryKey: folderKeys.detail(clientId, campaignId, id),
    queryFn: () => folderApi.getOneFolder({ clientId, campaignId, id }),
    enabled: !!id,
    ...options,
  });
};

/**
 * Mutation for creating a folder
 */
export const useCreateFolder = (contextOverrides?: { clientId?: string; campaignId?: string }) => {
  const queryClient = useQueryClient();
  const apiContext = useApiContext();
  const clientId = contextOverrides?.clientId ?? apiContext.clientId;
  const campaignId = contextOverrides?.campaignId ?? apiContext.campaignId;

  return useMutation({
    mutationFn: (data: CampaignFolderCreateRequest) => {
      if (!clientId || !campaignId) {
        throw new Error("Context required for useCreateFolder mutation");
      }
      return folderApi.createFolder({ clientId, campaignId }, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
      queryClient.invalidateQueries({ queryKey: touchpointKeys.all });
    },
  });
};

/**
 * Mutation for updating a folder
 */
export const useUpdateFolder = () => {
  const queryClient = useQueryClient();
  const { clientId, campaignId } = useRequiredApiContext();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CampaignFolderUpdateRequest }) =>
      folderApi.updateFolder({ clientId, campaignId, id }, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
      queryClient.invalidateQueries({ queryKey: touchpointKeys.all });
    },
  });
};

/**
 * Mutation for deleting a folder
 */
export const useDeleteFolder = () => {
  const queryClient = useQueryClient();
  const { clientId, campaignId } = useRequiredApiContext();

  return useMutation({
    mutationFn: (id: string) => folderApi.deleteFolder({ clientId, campaignId, id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
      queryClient.invalidateQueries({ queryKey: touchpointKeys.all });
    },
  });
};

export type AddTouchpointToFolderVariables = {
  folderId: string;
  data: AddToFolderRequest;
  meta?: BulkMutationMeta;
};

/**
 * Mutation for adding a touchpoint to a folder.
 * In bulk flows pass meta: { skipInvalidation: true } and invalidate once after the batch.
 */
export const useAddTouchpointToFolder = () => {
  const queryClient = useQueryClient();
  const { clientId, campaignId } = useRequiredApiContext();

  return useMutation({
    mutationFn: ({ folderId, data }: AddTouchpointToFolderVariables) =>
      folderApi.addTouchpointToFolder({ clientId, campaignId, id: folderId }, data),
    onSuccess: (_, variables) => {
      if (variables.meta?.skipInvalidation) return;
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
      queryClient.invalidateQueries({ queryKey: touchpointKeys.all });
    },
  });
};
